/**
 * The currency picker of a create form (docs/design/currency.md R3/R7,
 * CUR-2): every books currency, prefilled with what the API itself would
 * choose when nothing is sent, the workspace currency. Never a literal, and
 * never the customer's until the owner answers Q1.
 */
import { useMemo } from "react";
import type { CreateField } from "./ResourceListPage";
import type { FormField } from "./CreateRecordButton";
import { useTranslation } from "@/lib/i18n";
import { currencyPickerOptions, useWorkspaceCurrency } from "@/lib/currency";

function useChoices() {
  const { t, i18n } = useTranslation();
  const workspace = useWorkspaceCurrency();
  const options = useMemo(
    () => currencyPickerOptions(i18n.language, workspace),
    [i18n.language, workspace],
  );
  return { t, workspace, options };
}

/** For ResourceListPage `createFields`. */
export function useCurrencyCreateField(field = "currencyCode"): CreateField {
  const { t, workspace, options } = useChoices();
  return {
    field,
    label: t("money.currency"),
    hint: t("money.currencyHint"),
    options,
    initial: workspace,
    // Until the workspace currency has loaded nothing is picked; sent empty,
    // the API applies the workspace's itself.
    emptyOption: workspace ? undefined : t("money.chooseCurrency"),
  };
}

/** For CreateRecordButton `fields`. */
export function useCurrencyFormField(
  name = "currencyCode",
  required = false,
): FormField {
  const { t, workspace, options } = useChoices();
  return {
    name,
    label: t("money.currency"),
    type: "select",
    required,
    hint: t("money.currencyHint"),
    options,
    defaultValue: workspace,
  };
}
